import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import { InvoiceReadingPanel } from "./InvoiceReadingPanel";
import { DocumentGenerator } from "./DocumentGenerator";
import "./stand-documents.css";

type Category = {
  id: string;
  name: string;
  access_level: "operational" | "financial" | "restricted";
  retention_years: number;
  legal_hold_default: boolean;
  active: boolean;
};
type DocumentRow = {
  id: string;
  category_id: string;
  entity_type: string;
  category: string;
  title: string;
  original_name: string;
  storage_path: string;
  size_bytes: number;
  version_number: number;
  retention_until: string | null;
  created_at: string;
  status: "pending_review" | "confirmed" | "rejected" | "archived";
  legal_hold: boolean;
  reviewed_at: string | null;
  review_reason: string | null;
};
type DocumentLink = {
  id: string;
  document_id: string;
  entity_type: string;
  entity_id: string | null;
  label: string | null;
  created_at: string;
};
type Vehicle = {
  id: string;
  registration: string | null;
  make: string;
  model: string;
};
type Sale = { id: string; customer_name: string; sale_date: string };

const safeName = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "-");
const fileHash = async (file: File) =>
  Array.from(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", await file.arrayBuffer()),
    ),
  )
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("");
const size = (value: number) =>
  value < 1048576
    ? `${Math.ceil(value / 1024)} KB`
    : `${(value / 1048576).toFixed(1)} MB`;
const statusLabels = {
  pending_review: "Por rever",
  confirmed: "Confirmado",
  rejected: "Rejeitado",
  archived: "Arquivado",
};
const accessLabels = {
  operational: "Operacional",
  financial: "Financeiro",
  restricted: "Gestão documental",
};
const errorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) return error.message;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  )
    return error.message;
  return fallback;
};

export function StandDocuments({
  organizationId,
  canManage,
  canConfigure,
}: {
  organizationId: string;
  canManage: boolean;
  canConfigure: boolean;
}) {
  const [documents, setDocuments] = useState<DocumentRow[]>([]),
    [categories, setCategories] = useState<Category[]>([]),
    [links, setLinks] = useState<DocumentLink[]>([]),
    [vehicles, setVehicles] = useState<Vehicle[]>([]),
    [sales, setSales] = useState<Sale[]>([]),
    [query, setQuery] = useState(""),
    [statusFilter, setStatusFilter] = useState("active"),
    [linkType, setLinkType] = useState<"vehicle" | "sale">("vehicle"),
    [notice, setNotice] = useState<string | null>(null),
    [creating, setCreating] = useState(false),
    [configuring, setConfiguring] = useState(false),
    [selected, setSelected] = useState<DocumentRow | null>(null),
    [legalFormOpen, setLegalFormOpen] = useState(false),
    [generatorOpen, setGeneratorOpen] = useState(false),
    [busyAction, setBusyAction] = useState<string | null>(null);
  const load = async () => {
    const [docs, cats, docLinks, vehicleRows, saleRows] =
      await Promise.all([
        supabase
          .from("archive_documents")
          .select(
            "id,category_id,entity_type,category,title,original_name,storage_path,size_bytes,version_number,retention_until,created_at,status,legal_hold,reviewed_at,review_reason",
          )
          .eq("organization_id", organizationId)
          .order("created_at", { ascending: false }),
        supabase
          .from("document_categories")
          .select(
            "id,name,access_level,retention_years,legal_hold_default,active",
          )
          .eq("organization_id", organizationId)
          .order("name"),
        supabase
          .from("archive_document_links")
          .select("id,document_id,entity_type,entity_id,label,created_at")
          .eq("organization_id", organizationId)
          .order("created_at"),
        supabase
          .from("vehicles")
          .select("id,registration,make,model")
          .eq("organization_id", organizationId)
          .order("registration"),
        supabase
          .from("vehicle_sales")
          .select("id,customer_name,sale_date")
          .eq("organization_id", organizationId)
          .order("sale_date", { ascending: false }),
      ]);
    setDocuments((docs.data ?? []) as DocumentRow[]);
    setCategories((cats.data ?? []) as Category[]);
    setLinks((docLinks.data ?? []) as DocumentLink[]);
    setVehicles((vehicleRows.data ?? []) as Vehicle[]);
    setSales((saleRows.data ?? []) as Sale[]);
    const error =
      docs.error ??
      cats.error ??
      docLinks.error ??
      vehicleRows.error ??
      saleRows.error;
    if (error) setNotice(error.message);
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  const visible = useMemo(
    () =>
      documents
        .filter(
          (item) =>
            statusFilter === "all" ||
            (statusFilter === "active"
              ? item.status !== "archived"
              : item.status === statusFilter),
        )
        .filter((item) =>
          `${item.title} ${item.category} ${item.original_name}`
            .toLowerCase()
            .includes(query.toLowerCase()),
        ),
    [documents, query, statusFilter],
  );
  const upload = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusyAction("upload");
    setNotice("A guardar o documento…");
    const form = new FormData(event.currentTarget),
      file = form.get("file") as File,
      category = categories.find((item) => item.id === form.get("category"));
    let path = "";
    try {
      if (!category) throw new Error("Escolha uma categoria documental ativa.");
      const hash = await fileHash(file);
      path = `${organizationId}/stand/${crypto.randomUUID()}-${safeName(file.name)}`;
      const sent = await supabase.storage
        .from("stand-document-archive")
        .upload(path, file);
      if (sent.error) throw sent.error;
      const saved = await supabase.rpc("register_archive_document", {
        p_organization_id: organizationId,
        p_vehicle_id: null,
        p_entity_type: "stand",
        p_entity_id: null,
        p_category: category.name,
        p_title: String(form.get("title") || file.name),
        p_storage_path: path,
        p_original_name: file.name,
        p_mime_type: file.type || "application/octet-stream",
        p_size_bytes: file.size,
        p_sha256: hash,
        p_notes: form.get("notes"),
      });
      if (saved.error) throw saved.error;
      setCreating(false);
      setNotice("Documento guardado e enviado para revisão.");
      await load();
    } catch (error) {
      if (path)
        await supabase.storage.from("stand-document-archive").remove([path]);
      setNotice(errorMessage(error, "Não foi possível arquivar o documento."));
    } finally {
      setBusyAction(null);
    }
  };
  const open = async (item: DocumentRow) => {
    setBusyAction("open");
    setNotice("A preparar o documento…");
    const { data, error } = await supabase.functions.invoke(
      "open-archive-document",
      {
        body: { document_id: item.id, purpose: "Consulta no arquivo central" },
      },
    );
    if (error || !data?.signed_url) {
      setBusyAction(null);
      return setNotice(
        data?.error ?? error?.message ?? "Não foi possível abrir o documento.",
      );
    }
    window.open(data.signed_url, "_blank", "noopener,noreferrer");
    setNotice(
      "Documento aberto. A ligação expira em 60 segundos e o acesso ficou registado.",
    );
    setBusyAction(null);
    await load();
  };
  const saveCategory = async (
    event: FormEvent<HTMLFormElement>,
    category?: Category,
  ) => {
    event.preventDefault();
    setBusyAction(`category-${category?.id ?? "new"}`);
    const form = new FormData(event.currentTarget),
      result = await supabase.rpc("upsert_document_category", {
        p_organization_id: organizationId,
        p_category_id: category?.id ?? null,
        p_name: form.get("name"),
        p_access_level: form.get("access"),
        p_retention_years: Number(form.get("retention")),
        p_legal_hold_default: form.get("legal") === "on",
        p_active: form.get("active") === "on",
      });
    setNotice(result.error?.message ?? "Categoria documental guardada.");
    if (!result.error) await load();
    setBusyAction(null);
  };
  const changeStatus = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    setBusyAction("status");
    const form = new FormData(event.currentTarget),
      result = await supabase.rpc("set_archive_document_status", {
        p_document_id: selected.id,
        p_status: form.get("status"),
        p_reason: form.get("reason") || null,
        p_legal_hold: null,
      });
    setNotice(result.error?.message ?? "Estado documental atualizado.");
    if (!result.error) {
      await load();
      setSelected(null);
    }
    setBusyAction(null);
  };
  const changeLegalHold = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    const reason = String(new FormData(event.currentTarget).get("reason") || "").trim();
    if (!reason) return;
    setBusyAction("legal");
    const result = await supabase.rpc("set_archive_document_legal_hold", {
      p_document_id: selected.id,
      p_legal_hold: !selected.legal_hold,
      p_reason: reason,
    });
    setNotice(
      result.error?.message ??
        (selected.legal_hold
          ? "Proteção jurídica terminada."
          : "Proteção jurídica aplicada."),
    );
    if (!result.error) {
      await load();
      setSelected({ ...selected, legal_hold: !selected.legal_hold });
      setLegalFormOpen(false);
    }
    setBusyAction(null);
  };
  const addLink = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    setBusyAction("link");
    const form = new FormData(event.currentTarget),
      type = String(form.get("entity_type")),
      id = String(form.get("entity_id") || "") || null;
    const result = await supabase.rpc("link_archive_document", {
      p_document_id: selected.id,
      p_entity_type: type,
      p_entity_id: id,
      p_label: type === "vehicle" ? "Viatura" : "Venda",
    });
    setNotice(
      result.error?.message ?? "Documento associado sem duplicar o ficheiro.",
    );
    if (!result.error) await load();
    setBusyAction(null);
  };
  const selectedLinks = selected
    ? links.filter((link) => link.document_id === selected.id)
    : [];
  return (
    <section className="stand-documents">
      <header>
        <div>
          <p className="eyebrow">Documentos operacionais</p>
          <h2>Arquivo central</h2>
          <p>
            Cada original é guardado uma única vez, com acesso, retenção, estado
            e vínculos auditáveis.
          </p>
        </div>
        <div className="archive-header-actions">
          <button className="stock-secondary" onClick={() => setGeneratorOpen(true)}>
            Gerar documentos
          </button>
          {canConfigure && (
            <button
              className="stock-secondary"
              onClick={() => setConfiguring(true)}
            >
              Categorias
            </button>
          )}
          {canManage && (
            <button className="stock-primary" onClick={() => setCreating(true)}>
              + Arquivar documento
            </button>
          )}
        </div>
      </header>
      {notice && (
        <button
          className="stock-notice"
          role="status"
          aria-live="polite"
          onClick={() => setNotice(null)}
        >
          {notice} ×
        </button>
      )}
      <div className="archive-toolbar">
        <input
          className="archive-search"
          placeholder="Filtrar por nome, categoria ou ficheiro"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="active">Em utilização</option>
          <option value="pending_review">Por rever</option>
          <option value="confirmed">Confirmados</option>
          <option value="rejected">Rejeitados</option>
          <option value="archived">Arquivados</option>
          <option value="all">Todos os estados</option>
        </select>
      </div>
      <div className="archive-summary">
        {Object.entries(statusLabels).map(([key, label]) => (
          <article key={key}>
            <span>{label}</span>
            <strong>
              {documents.filter((item) => item.status === key).length}
            </strong>
          </article>
        ))}
      </div>
      <div className="archive-list">
        {visible.length ? (
          visible.map((item) => (
            <button key={item.id} onClick={() => setSelected(item)}>
              <span>
                <strong>{item.title}</strong>
                <small>
                  {item.category} · versão {item.version_number} ·{" "}
                  {links.filter((link) => link.document_id === item.id).length}{" "}
                  vínculo(s)
                </small>
              </span>
              <span>
                <b data-status={item.status}>{statusLabels[item.status]}</b>
                <small>
                  {item.legal_hold
                    ? "Proteção jurídica"
                    : item.retention_until
                      ? `Retenção até ${new Date(item.retention_until).toLocaleDateString("pt-PT")}`
                      : "Sem retenção definida"}
                </small>
              </span>
            </button>
          ))
        ) : (
          <p>Não existem documentos neste filtro.</p>
        )}
      </div>
      {creating && (
        <div className="stock-dialog-backdrop">
          <section className="stock-dialog archive-dialog">
            <header>
              <div>
                <p className="eyebrow">Novo documento</p>
                <h2>Arquivo central</h2>
                <p>O ficheiro ficará privado e entra no estado Por rever.</p>
              </div>
              <button
                className="stock-close"
                onClick={() => setCreating(false)}
              >
                ×
              </button>
            </header>
            <form onSubmit={upload}>
              <label>
                <span>Categoria *</span>
                <select name="category" required defaultValue="">
                  <option value="" disabled>
                    Escolha a categoria
                  </option>
                  {categories
                    .filter((item) => item.active)
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} · {accessLabels[item.access_level]}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                <span>Nome do ficheiro</span>
                <input
                  name="title"
                  placeholder="Se ficar vazio, será usado o nome original"
                />
              </label>
              <label>
                <span>Ficheiro *</span>
                <input name="file" type="file" required />
              </label>
              <label>
                <span>Notas</span>
                <textarea name="notes" />
              </label>
              <footer>
                <button
                  type="button"
                  className="stock-secondary"
                  onClick={() => setCreating(false)}
                >
                  Cancelar
                </button>
                <button className="stock-primary" disabled={busyAction !== null}>
                  {busyAction === "upload" ? "A guardar…" : "Guardar"}
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}
      {generatorOpen && (
        <DocumentGenerator
          organizationId={organizationId}
          canManage={canManage}
          canConfigure={canConfigure}
          onClose={() => setGeneratorOpen(false)}
          onChanged={load}
        />
      )}
      {configuring && (
        <div className="stock-dialog-backdrop">
          <section className="stock-dialog archive-dialog archive-category-dialog">
            <header>
              <div>
                <p className="eyebrow">Configuração</p>
                <h2>Categorias documentais</h2>
                <p>
                  Defina quem consulta cada categoria. Política de retenção
                  documental e proteção jurídica inicial são aplicadas a todos
                  os novos documentos dessa categoria.
                </p>
              </div>
              <button
                className="stock-close"
                onClick={() => setConfiguring(false)}
              >
                ×
              </button>
            </header>
            <div className="archive-category-list">
              {categories.map((category) => (
                <form
                  key={category.id}
                  onSubmit={(event) => saveCategory(event, category)}
                >
                  <input
                    name="name"
                    defaultValue={category.name}
                    aria-label="Nome da categoria"
                  />
                  <select
                    name="access"
                    defaultValue={category.access_level}
                    aria-label="Quem pode consultar"
                  >
                    <option value="operational">Operacional</option>
                    <option value="financial">Financeiro</option>
                    <option value="restricted">Gestão documental</option>
                  </select>
                  <label>
                    <span>Guardar durante</span>
                    <input
                      name="retention"
                      type="number"
                      min="1"
                      max="30"
                      defaultValue={category.retention_years}
                    />
                    <small>anos</small>
                  </label>
                  <label className="plain-check">
                    <input
                      name="legal"
                      type="checkbox"
                      defaultChecked={category.legal_hold_default}
                    />
                    Impedir eliminação por proteção jurídica
                  </label>
                  <label className="plain-check">
                    <input
                      name="active"
                      type="checkbox"
                      defaultChecked={category.active}
                    />
                    Ativa
                  </label>
                  <button className="stock-primary" disabled={busyAction !== null}>
                    Guardar
                  </button>
                </form>
              ))}
              <form
                className="archive-new-category"
                onSubmit={(event) => saveCategory(event)}
              >
                <input name="name" placeholder="Nova categoria" required />
                <select name="access" defaultValue="operational">
                  <option value="operational">Operacional</option>
                  <option value="financial">Financeiro</option>
                  <option value="restricted">Gestão documental</option>
                </select>
                <label>
                  <span>Guardar durante</span>
                  <input
                    name="retention"
                    type="number"
                    min="1"
                    max="30"
                    defaultValue="10"
                  />
                  <small>anos</small>
                </label>
                <label className="plain-check">
                  <input name="legal" type="checkbox" />
                  Impedir eliminação por proteção jurídica
                </label>
                <label className="plain-check">
                  <input name="active" type="checkbox" defaultChecked />
                  Ativa
                </label>
                <button className="stock-primary" disabled={busyAction !== null}>
                  Guardar
                </button>
              </form>
            </div>
          </section>
        </div>
      )}
      {selected && (
        <div className="stock-dialog-backdrop">
          <section className="stock-dialog archive-dialog archive-detail-dialog">
            <header>
              <div>
                <p className="eyebrow">Documento</p>
                <h2>{selected.title}</h2>
                <p>
                  {selected.category} · {size(selected.size_bytes)} · versão{" "}
                  {selected.version_number}
                </p>
              </div>
              <button
                className="stock-close"
                onClick={() => {
                  setSelected(null);
                  setLegalFormOpen(false);
                }}
              >
                ×
              </button>
            </header>
            <div className="archive-detail-body">
              <div className="archive-detail-state">
                <b data-status={selected.status}>
                  {statusLabels[selected.status]}
                </b>
                <span>
                  {selected.legal_hold
                    ? "Proteção jurídica ativa"
                    : selected.retention_until
                      ? `Retenção até ${new Date(selected.retention_until).toLocaleDateString("pt-PT")}`
                      : "Sem retenção definida"}
                </span>
                <button
                  className="stock-primary"
                  disabled={busyAction !== null}
                  onClick={() => void open(selected)}
                >
                  {busyAction === "open" ? "A abrir…" : "Abrir documento"}
                </button>
                {canManage && (
                  <button
                    className="stock-secondary"
                    disabled={busyAction !== null}
                    onClick={() => setLegalFormOpen((current) => !current)}
                  >
                    {selected.legal_hold
                      ? "Terminar proteção jurídica"
                      : "Aplicar proteção jurídica"}
                  </button>
                )}
              </div>
              {canManage && legalFormOpen && (
                <form className="archive-legal-form" onSubmit={changeLegalHold}>
                  <label>
                    <span>
                      {selected.legal_hold
                        ? "Motivo para terminar a proteção jurídica"
                        : "Motivo para aplicar a proteção jurídica"}
                    </span>
                    <textarea name="reason" required autoFocus />
                  </label>
                  <div>
                    <button
                      type="button"
                      className="stock-secondary"
                      onClick={() => setLegalFormOpen(false)}
                    >
                      Cancelar
                    </button>
                    <button className="stock-primary" disabled={busyAction !== null}>
                      {busyAction === "legal" ? "A guardar…" : "Guardar"}
                    </button>
                  </div>
                </form>
              )}
              <section>
                <h3>Vínculos</h3>
                {selectedLinks.map((link) => (
                  <p key={link.id}>
                    {link.entity_type === "stand"
                      ? "Stand"
                      : (link.label ?? link.entity_type)}{" "}
                    · {link.entity_id ?? "arquivo geral"}
                  </p>
                ))}
                {canManage && (
                  <form className="archive-link-form" onSubmit={addLink}>
                    <select
                      name="entity_type"
                      value={linkType}
                      onChange={(event) =>
                        setLinkType(event.target.value as "vehicle" | "sale")
                      }
                    >
                      <option value="vehicle">Viatura</option>
                      <option value="sale">Venda</option>
                    </select>
                    <select name="entity_id" required>
                      {linkType === "vehicle"
                        ? vehicles.map((vehicle) => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {vehicle.registration ?? "Sem matrícula"} ·{" "}
                            {vehicle.make} {vehicle.model}
                          </option>
                          ))
                        : sales.map((sale) => (
                          <option key={sale.id} value={sale.id}>
                            {sale.customer_name} ·{" "}
                            {new Date(sale.sale_date).toLocaleDateString(
                              "pt-PT",
                            )}
                          </option>
                          ))}
                    </select>
                    <button
                      className="stock-secondary"
                      disabled={busyAction !== null}
                    >
                      Associar
                    </button>
                  </form>
                )}
              </section>
              {canManage && selected.category === "Fatura" && (
                <InvoiceReadingPanel
                  documentId={selected.id}
                  vehicles={vehicles}
                  onNotice={setNotice}
                />
              )}
              {canManage && (
                <form className="archive-review-form" onSubmit={changeStatus}>
                  <h3>Revisão</h3>
                  <select
                    name="status"
                    defaultValue={
                      selected.status === "pending_review"
                        ? "confirmed"
                        : selected.status
                    }
                  >
                    <option value="confirmed">Confirmado</option>
                    <option value="rejected">Rejeitado</option>
                    <option value="archived">Arquivado</option>
                  </select>
                  <textarea
                    name="reason"
                    placeholder="Motivo obrigatório para rejeitar ou arquivar"
                  />
                  <button className="stock-primary" disabled={busyAction !== null}>
                    {busyAction === "status" ? "A guardar…" : "Guardar"}
                  </button>
                </form>
              )}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
